<template>
  <div class="min-w-full">
    <RevCheckbox
      :id="
        (isFree ? 'free-' : 'paid-') +
        marketWithSubMarket.market +
        '-shipping-rule'
      "
      :key="
        (isFree ? 'free-' : 'paid-') +
        marketWithSubMarket.market +
        '-shipping-rule'
      "
      :checked="hasAllSubMarketsSelected"
      class="mb-12"
      @click="handleSelectAll"
    >
      <template #label>
        <div class="flex items-center gap-8">
          <FlagDisplayer :market="marketWithSubMarket.market" />

          <span>{{ i18n(translations.allSubMarkets) }}</span>
        </div>
      </template>
    </RevCheckbox>

    <div class="border-static-default-low min-w-full space-y-12 border-t pt-12">
      <div
        v-for="(value, key) in regions"
        :key
        class="bg-surface-default-mid border-static-default-mid rounded-md border"
      >
        <button
          :key
          class="flex w-full items-center justify-between p-24"
          @click="toggleExpandRegion(key)"
        >
          <RevCheckbox
            :id="
              (isFree ? 'free-' : 'paid-') +
              marketWithSubMarket.market +
              '-' +
              key +
              '-shipping-rule'
            "
            :key="
              (isFree ? 'free-' : 'paid-') +
              marketWithSubMarket.market +
              '-' +
              key +
              '-shipping-rule'
            "
            :checked="hasAllSubMarketsOfRegionSelected(key)"
            :disabled="isRegionDisabled(key)"
            :value="key"
            @click.self="handleSelectAllSubMarketsOfRegion(key)"
          >
            <template #label>
              {{ key }}
            </template>
          </RevCheckbox>

          <IconChevronDown
            :class="{ 'rotate-180': hasExpended.includes(key) }"
          />
        </button>

        <div
          class="border-static-default-low grid min-w-full grid-cols-2 gap-y-12 border-t p-24 pt-24 md:grid-cols-4"
          :class="{ hidden: !hasExpended.includes(key) }"
        >
          <RevCheckbox
            v-for="subMarket in value"
            :id="
              (isFree ? 'free-' : 'paid-') +
              key +
              '-' +
              subMarket.subMarketCode +
              '-shipping-rule'
            "
            :key="
              (isFree ? 'free-' : 'paid-') +
              key +
              '-' +
              subMarket.subMarketCode +
              '-shipping-rule'
            "
            v-model="selectedSubMarkets"
            :disabled="isSubMarketDisabled(subMarket.subMarketCode)"
            :value="subMarket.subMarketCode"
            @click="handleSelectSubMarket"
          >
            <template #label>
              <div class="body-2-bold flex items-center gap-4">
                <span>{{ subMarket.subMarketName }}</span>

                <RevTooltip
                  v-if="
                    isSubMarketDisabledForPaidMethods(subMarket.subMarketCode)
                  "
                  position="top"
                >
                  <template #trigger="{ hide, show }">
                    <IconInfo
                      class="align-middle"
                      @focusin="show"
                      @focusout="hide"
                      @mouseleave="hide"
                      @mouseover="show"
                    />
                  </template>

                  {{
                    i18n(
                      ruleDrawerTranslations.errorPaidMethodTimeNotFastEnough,
                    )
                  }}
                </RevTooltip>
              </div>
            </template>
          </RevCheckbox>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type {
  MarketWithSubMarkets,
  SubMarket,
} from '@backmarket/http-api/src/api-specs-shipping/rules'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconInfo } from '@ds/icons/IconInfo'

import FlagDisplayer from '../FlagDisplayer/FlagDisplayer.vue'
import { MIN_SHIPPING_TIME } from '../RuleDrawer/RuleDrawer.constants'
import type { FormMethodValues } from '../RuleDrawer/RuleDrawer.types.js'
import { getMinShippingTime } from '../RuleDrawer/RuleDrawer.utils.js'
import ruleDrawerTranslations from '../RuleDrawer/components/RuleFormFields/RuleFormFields.translations'

import translations from './ToggleMarket.translations'

const i18n = useI18n()

const props = defineProps<{
  marketWithSubMarket: MarketWithSubMarkets
  currentSelection: MarketWithSubMarkets[]
  currentMethods: FormMethodValues[]
  otherMethods: FormMethodValues[]
  isFree: boolean
}>()

const emit = defineEmits(['selected'])

const selectedSubMarkets = ref<string[]>([])
const hasAllSelected = ref(false)
const hasExpended = ref<string[]>([])

const regions = computed(() => {
  const grouped: Record<string, SubMarket[]> = {}

  for (const state of props.marketWithSubMarket.subMarkets) {
    const region = state.countryRegion
    if (!grouped[region]) {
      grouped[region] = []
    }
    grouped[region].push(state)
  }

  Object.keys(grouped).forEach((region) => {
    grouped[region].sort((a, b) =>
      a.subMarketName.localeCompare(b.subMarketName),
    )
  })

  return grouped
})

const hasAllSubMarketsSelected = computed(() => {
  return props.marketWithSubMarket.subMarkets.every((subMarket) =>
    selectedSubMarkets.value.includes(subMarket.subMarketCode),
  )
})

function isSubMarketAlreadyInUseInOtherMethod(subMarketCode: string) {
  return (
    props.isFree &&
    props.currentMethods.some(({ markets }) => {
      return markets.some((marketWithSub) =>
        marketWithSub.subMarkets.some(
          (subMarket) => subMarket.subMarketCode === subMarketCode,
        ),
      )
    })
  )
}

function isSubMarketDisabledForPaidMethods(subMarketCode: string) {
  if (props.isFree) {
    return false
  }

  const minimumShippingTimeForMarket = getMinShippingTime(props.otherMethods, [
    subMarketCode,
  ])

  return (
    minimumShippingTimeForMarket &&
    minimumShippingTimeForMarket <= MIN_SHIPPING_TIME
  )
}

function isSubMarketDisabled(subMarketCode: string) {
  return (
    isSubMarketDisabledForPaidMethods(subMarketCode) ||
    isSubMarketAlreadyInUseInOtherMethod(subMarketCode)
  )
}

function isRegionDisabled(state: string) {
  return regions.value[state].every(
    (subMarket) =>
      isSubMarketAlreadyInUseInOtherMethod(subMarket.subMarketCode) ||
      isSubMarketDisabledForPaidMethods(subMarket.subMarketCode),
  )
}

function hasAllSubMarketsOfRegionSelected(
  state: string,
  includesDisabled = true,
) {
  if (includesDisabled) {
    return regions.value[state].every((subMarket) =>
      selectedSubMarkets.value.includes(subMarket.subMarketCode),
    )
  }

  return regions.value[state]
    .filter((subMarket) => !isSubMarketDisabled(subMarket.subMarketCode))
    .every((subMarket) =>
      selectedSubMarkets.value.includes(subMarket.subMarketCode),
    )
}

function emitSelection() {
  emit('selected', {
    market: props.marketWithSubMarket.market,
    subMarkets: props.marketWithSubMarket.subMarkets.filter((subMarket) => {
      return (
        selectedSubMarkets.value.indexOf(subMarket.subMarketCode) !== -1 &&
        !isSubMarketAlreadyInUseInOtherMethod(subMarket.subMarketCode)
      )
    }),
  })
}

function toggleExpandRegion(key: string) {
  if (hasExpended.value.includes(key)) {
    hasExpended.value = hasExpended.value.filter((k) => k !== key)
  } else {
    hasExpended.value.push(key)
  }
}

function handleSelectAll() {
  hasAllSelected.value = !hasAllSelected.value

  if (hasAllSelected.value) {
    selectedSubMarkets.value = props.marketWithSubMarket.subMarkets
      .map((subMarket) => subMarket.subMarketCode)
      .filter((subMarketCode) => !isSubMarketDisabled(subMarketCode))
  } else {
    selectedSubMarkets.value = selectedSubMarkets.value.filter(
      (subMarketCode) => isSubMarketAlreadyInUseInOtherMethod(subMarketCode),
    )
  }
}

function handleSelectAllSubMarketsOfRegion(state: string) {
  if (!hasAllSubMarketsOfRegionSelected(state, false)) {
    regions.value[state].forEach((subMarket) => {
      if (!isSubMarketDisabled(subMarket.subMarketCode)) {
        selectedSubMarkets.value.push(subMarket.subMarketCode)
      }
    })
  } else {
    regions.value[state].forEach((subMarket) => {
      selectedSubMarkets.value = selectedSubMarkets.value.filter(
        (subMarketCode) =>
          subMarketCode !== subMarket.subMarketCode ||
          isSubMarketAlreadyInUseInOtherMethod(subMarketCode),
      )
    })
  }
}

function handleSelectSubMarket() {
  hasAllSelected.value = false
}

watch(
  () => props.currentMethods,
  () => {
    if (props.isFree) {
      props.currentMethods.forEach((method) => {
        method.markets.forEach((marketWithSubMarkets) => {
          marketWithSubMarkets.subMarkets.forEach((subMarket) => {
            if (!selectedSubMarkets.value.includes(subMarket.subMarketCode)) {
              selectedSubMarkets.value.push(subMarket.subMarketCode)
            }
          })
        })
      })
    }
  },
  {
    immediate: true,
  },
)

watch(() => selectedSubMarkets.value, emitSelection, { deep: true })
</script>
